import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 15000,
});

// Attach JWT token to every request
API.interceptors.request.use(config => {
  const token = localStorage.getItem('korpnex_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, error => Promise.reject(error));

// Handle 401 globally (auto-logout)
API.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('korpnex_token');
      localStorage.removeItem('korpnex_user');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ──
export const sendOTP        = data => API.post('/auth/send-otp', data);      // NEW — send OTP
export const registerUser   = data => API.post('/auth/register', data);
export const loginUser      = data => API.post('/auth/login', data);
export const getMe          = ()   => API.get('/auth/me');
export const updateProfile  = data => API.put('/auth/update-profile', data);
export const changePassword = data => API.put('/auth/change-password', data);
export const forgotPassword = data => API.post('/auth/forgot-password', data);//new added
export const resetPassword  = data => API.post('/auth/reset-password', data);//new added

// ── Courses ──
export const getCourses = () => API.get('/courses');
export const getCourse = slug => API.get(`/courses/${slug}`);

// ── Applications ──
export const submitApplication = data => API.post('/applications', data);
export const getMyApplications = () => API.get('/applications/my');

// ── Payments ──
export const createOrder  = data => API.post('/payments/create-order', data);
export const verifyPayment = data => API.post('/payments/verify', data);
export const getMyPayments = () => API.get('/payments/my');
export const getReceipt   = id => API.get(`/payments/receipt/${id}`);

// ── Dashboard ──
export const getDashboardOverview = () => API.get('/dashboard/overview');

// ── Testimonials ──
export const getTestimonials = () => API.get('/testimonials');

// ── Admin ──
export const getAdminStats            = ()          => API.get('/admin/stats');
export const getAdminApplications     = (params)   => API.get('/admin/applications', { params });
export const updateApplicationStatus  = (id, data) => API.put(`/admin/applications/${id}/status`, data);
export const getAdminPayments         = (params)   => API.get('/admin/payments', { params });
export const updatePaymentStatus      = (id, data) => API.put(`/admin/payments/${id}/status`, data);
export const getAdminUsers            = ()          => API.get('/admin/users');
export const getAdminCourses          = ()          => API.get('/courses');
export const createCourse             = data        => API.post('/courses', data);
export const updateCourse             = (id, data) => API.put(`/courses/${id}`, data);
export const deleteCourse             = id          => API.delete(`/courses/${id}`);
export const getAdminTestimonials     = ()          => API.get('/admin/testimonials');
export const createTestimonial        = data        => API.post('/admin/testimonials', data);
export const updateTestimonial        = (id, data) => API.put(`/admin/testimonials/${id}`, data);
export const deleteTestimonial        = id          => API.delete(`/admin/testimonials/${id}`);

// ── LMS (Student) ──
export const getMyEnrollments = ()    => API.get('/lms/my-enrollments');
export const getMyEnrollment  = id   => API.get(`/lms/my-enrollments/${id}`);
export const markItemComplete = (enrollmentId, itemId) => API.post(`/lms/my-enrollments/${enrollmentId}/complete/${itemId}`);
export const submitAssignment = (enrollmentId, itemId, data) => API.post(`/lms/my-enrollments/${enrollmentId}/submit-assignment/${itemId}`, data);

// ── LMS (Admin) ──
export const lmsSearchStudents        = q          => API.get(`/lms/admin/search-students?q=${encodeURIComponent(q)}`);
export const lmsGetStudentEnrollments = studentId  => API.get(`/lms/admin/student/${studentId}/enrollments`);
export const lmsGetAllEnrollments     = params     => API.get('/lms/admin/all-enrollments', { params });
export const lmsEnrollStudent         = data       => API.post('/lms/admin/enroll', data);
export const lmsGetEnrollment         = id         => API.get(`/lms/admin/enrollment/${id}`);
export const lmsUpdateEnrollmentInfo  = (id, data) => API.put(`/lms/admin/enrollment/${id}/info`, data);
export const lmsAddModule             = (enrollmentId, data)                    => API.post(`/lms/admin/enrollment/${enrollmentId}/modules`, data);
export const lmsUpdateModule          = (enrollmentId, moduleId, data)          => API.put(`/lms/admin/enrollment/${enrollmentId}/modules/${moduleId}`, data);
export const lmsDeleteModule          = (enrollmentId, moduleId)                => API.delete(`/lms/admin/enrollment/${enrollmentId}/modules/${moduleId}`);
export const lmsAddItem               = (enrollmentId, moduleId, data)          => API.post(`/lms/admin/enrollment/${enrollmentId}/modules/${moduleId}/items`, data);
export const lmsUpdateItem            = (enrollmentId, moduleId, itemId, data)  => API.put(`/lms/admin/enrollment/${enrollmentId}/modules/${moduleId}/items/${itemId}`, data);
export const lmsDeleteItem            = (enrollmentId, moduleId, itemId)        => API.delete(`/lms/admin/enrollment/${enrollmentId}/modules/${moduleId}/items/${itemId}`);
export const lmsGradeAssignment       = (enrollmentId, moduleId, itemId, data)  => API.post(`/lms/admin/enrollment/${enrollmentId}/modules/${moduleId}/items/${itemId}/grade`, data);

// ── Live Classes ──
export const getLiveClasses      = ()       => API.get('/live/my-classes');
export const getLiveClass        = id       => API.get(`/live/my-classes/${id}`);
export const getAdminLiveClasses = ()       => API.get('/live/admin/classes');
export const createLiveClass     = data     => API.post('/live/admin/classes', data);
export const updateLiveClass     = (id, d)  => API.put(`/live/admin/classes/${id}`, d);
export const deleteLiveClass     = id       => API.delete(`/live/admin/classes/${id}`);
export const addReplayLink       = (id, d)  => API.post(`/live/admin/classes/${id}/replay`, d);
export const getAdminTeachers    = ()       => API.get('/live/admin/teachers');
export const createTeacher       = data     => API.post('/live/admin/teachers', data);
export const updateTeacher       = (id, d)  => API.put(`/live/admin/teachers/${id}`, d);
export const deleteTeacher       = id       => API.delete(`/live/admin/teachers/${id}`);
export const teacherGetMyClasses = ()       => API.get('/live/teacher/my-classes');
export const teacherStartClass   = id       => API.post(`/live/teacher/classes/${id}/start`);
export const teacherEndClass     = id       => API.post(`/live/teacher/classes/${id}/end`);
export const teacherAddReplay    = (id, d)  => API.post(`/live/teacher/classes/${id}/replay`, d);

export default API;